import { Link } from 'react-router-dom';
import { emphasize } from '../lib/emphasis.js';

/**
 * Inner-page header. Matches `.page-header` (with optional `.with-image`)
 * from design-source/styles.css.
 *
 *   crumbs:    [{ label, to? }]   last item is the current page (no link)
 *   title:     plain text fallback for the H1, OR
 *   headline:  display title with *asterisk* emphasis (preferred — CMS field), OR
 *   rawTitle:  raw HTML string (legacy; use <em>...</em> for italic accent)
 *   lede:      optional supporting paragraph
 *   image:     optional URL — switches to `.with-image` variant
 *   imageFrame: optional bool — draws a gold corner accent (matches
 *               `.leader-portrait::after`) over the banner image
 */
export function PageHeader({ crumbs = [], title, rawTitle, headline, lede, image, imageFrame = false }) {
  const framed = !!image && imageFrame;
  const cls = `page-header${image ? ' with-image' : ''}${framed ? ' with-accent' : ''}`;
  const style = image ? { ['--bg-img']: `url('${image}')` } : undefined;
  // Precedence: asterisk headline → legacy rawTitle HTML → plain title.
  const titleHtml = headline ? emphasize(headline) : rawTitle;

  return (
    <section className={cls} style={style}>
      {framed && <span className="ph-accent" aria-hidden="true" />}
      <div className="container">
        {crumbs.length > 0 && (
          <div className="breadcrumbs">
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <span key={i}>
                  {last || !c.to ? <span>{c.label}</span> : <Link to={c.to}>{c.label}</Link>}
                  {!last && <span className="sep">›</span>}
                </span>
              );
            })}
          </div>
        )}
        {titleHtml ? <h1 dangerouslySetInnerHTML={{ __html: titleHtml }} /> : <h1>{title}</h1>}
        {lede && <p className="lede">{lede}</p>}
      </div>
    </section>
  );
}
